import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { IData, IDataResponse } from 'models/order';
import { Controller, useForm } from 'react-hook-form';
import useHttp from 'utils/hooks/useHttp';
import TextInput from 'components/inputs/TextInput/TextInput';
import DefaultButton from 'components/buttons/DefaultButton/DefaultButton';
import DatePicker from 'react-datepicker';
import { useOnClickOutside } from 'utils/hooks/useOnClickOutside';
import moment from 'moment';

import 'react-datepicker/dist/react-datepicker.css';
import OrderTable from './ClientOrderTable/ClientOrderTable';

import { useTranslation } from 'react-i18next';
import { useDeviceDetect } from 'utils/hooks/deviceDetect';
import { useLocaleDatePicker } from 'utils/localeDatePicker';
import { useNavigate, useSearchParams } from 'react-router-dom';
import i18n from 'i18n';
import Select from 'react-select';
import './ClientOrderList.scss';
import ClientOrderDetails from './ClientOrderDetails/ClientOrderDetails';
import { IOption } from 'interfaces/select';
import { usePermission } from 'utils/usePermission';
import { ROLE_SELLER } from 'utils/constants/roles';


type Props = {
  catalogId?: string;
  hideFooter?: boolean
  showRoleSeller?: boolean
};

const ClientOrderList: React.FC<Props> = (props) => {
  const { setValue, getValues, register, handleSubmit, reset, control } = useForm();
  const http = useHttp();
  const navigate = useNavigate();
  const [urlParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<IData[]>([]);
  const [clients, setClients] = useState<IOption[]>([]);
  const [openDetails, setOpenDetails] = useState(false);
  const [orderAction, setOrderAction] = useState<string>('');

  const [, setPaginationData] = useState<IDataResponse>();
  const [currentPage, setCurrentPage] = useState<number>(urlParams.get('page') ? Number(urlParams.get('page')) : 1);
  const [totalPage, setTotalPage] = useState<number>(0);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [valueDate, setValueDate] = useState('');
  const [openDate, setOpenDate] = useState(false);
  const [loading, setLoading] = useState(false);
  const dateRef = useRef() as MutableRefObject<HTMLDivElement>;

  const currentSegment = location.pathname.split('/')[1];
  const segments = location.pathname.split('/').filter(Boolean);
  // luăm primele 3 segmente și construim baza
  const basePath = '/' + segments.slice(0, 3).join('/');

  const { t } = useTranslation();
  const { isMobile } = useDeviceDetect();

  useOnClickOutside(dateRef, () => setOpenDate(false));
  useLocaleDatePicker(i18n.language);


  let COMMERCIAL_DIRECTOR_STATUSES: { label: string; value: string }[] = [];

  switch (true) {

    case usePermission([ROLE_SELLER]):
    case props.showRoleSeller:
      COMMERCIAL_DIRECTOR_STATUSES = [
        { label: t('_pending_seller'), value: 'WAIT_CONFIRMATION_SELLER' },
        { label: t('_rejected'), value: 'REJECTED_BY_DIRECTOR_COMMERCIAL' },
      ];

      break;

    default:
      COMMERCIAL_DIRECTOR_STATUSES = [
        { label: t('_pending'), value: 'WAIT_CONFIRMATION_DIRECTOR_COMMERCIAL' },
        { label: t('_rejected'), value: 'REJECTED_BY_DIRECTOR_COMMERCIAL' },
      ];

      break;
  }

  function getClientOrderList(params) {
    setLoading(true);

    const paramsNew = { ...params, status: getValues('status')?.value };

    if (location.pathname.includes('/waiting-confirmation') || location.pathname.includes('/waiting-seller-confirmation')) {
      paramsNew.seller_view = 1;
    }

    http.order
      .getOrders(paramsNew)
      .then((res) => {
        const { data, page, total_pages } = res.data as IDataResponse;

        setTotalPage(total_pages);
        setData(data);
        setPaginationData({ data, page, total_pages });

      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {

    setValue('status', COMMERCIAL_DIRECTOR_STATUSES[0]);
  }, []);


  useEffect(() => {


    getClientOrderList({ page: urlParams.get('page') ?? 1, status: getValues('status')?.value });


  }, [urlParams.get('page')]);

  const onSubmit = async () => {
    setLoading(true);
    const data = getValues();

    if (data?.status) {
      data.status = data.status?.value;
    }

    if (data?.customer_id) {
      data.customer_id = data.customer_id?.value;
    }

    const start_date = moment(startDate).format('YYYY-MM-DD');
    const end_date = moment(endDate).format('YYYY-MM-DD');

    if (moment(start_date).isValid()) {
      data.start_date = moment(startDate).format('YYYY-MM-DD');
    }

    if (moment(end_date).isValid()) {
      data.end_date = moment(endDate).format('YYYY-MM-DD');
    }


    const validParams = Object.entries(data).reduce((acc, [key, value]) => {
      if (!value) {
        return acc;
      }

      acc[key] = value;

      return acc;
    }, {});

    urlParams.delete('page');
    setSearchParams(urlParams);
    setCurrentPage(1);
    await getClientOrderList({ ...validParams, page: 1 });
  };

  const handlePageClick = (data: number) => {
    setCurrentPage(data);
  };

  function onDateChange(dates) {
    const [start, end] = dates;

    setStartDate(start);
    setEndDate(end);

    if (start && end) {
      setValueDate(`${moment(start).format('DD/MM/YYYY')} - ${moment(end).format('DD/MM/YYYY')}`);
    }
    else {

      if (start) {
        setValueDate(`${moment(start).format('DD/MM/YYYY')}`);

      }

      if (end) {
        setValueDate(`${moment(end).format('DD/MM/YYYY')}`);
      }
    }
  }

  function getClientOptions() {
    http.order.getClients().then(res => {
      setClients(res.data);
    });
  }

  useEffect(() => {
    getClientOptions();
  }, []);

  const handleResetFilter = () => {

    reset({ status: COMMERCIAL_DIRECTOR_STATUSES[0], date: '', id: '', client: '', customer_id: '' });
    setCurrentPage(1);
    setSearchParams({ page: '1' });
    setStartDate(null);
    setEndDate(null);
    setValueDate('');
  };

  const hiddenColumns = isMobile ? { hiddenColumns: ['time', 'proforma_id', 'user_email'] } : {};


  return (
    !openDetails ?


      <div className="automotor-orders-list-client">

        <div className="orders-filter">
          <div className="orders-filter-header">

            <div className="filter-title">{t('_order_search')}</div>
            <DefaultButton text={t('_orders_status_history')} className="btn_history" onClick={() => { props.hideFooter ? navigate(`${basePath}/history-orders`) : navigate(`/${currentSegment}/history-orders`); }} />
          </div>
          <form onSubmit={handleSubmit(() => onSubmit())}>
            <div className="orders-filter-body">
              <div className="orders-filter-input-item">
                <Controller
                  control={control}
                  name="status"
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Select
                        value={value}
                        className="select-dropdown"
                        onChange={(value) => onChange(value)}
                        options={COMMERCIAL_DIRECTOR_STATUSES}
                        placeholder={t('_status')}
                        styles={{
                          menu: provided => ({ ...provided, zIndex: 50 })
                        }}
                        isSearchable={!isMobile}
                      />
                    );
                  }}
                />
              </div>
              <div className="orders-filter-input-item">
                <div className={`orders-react-picker-dropdown ${openDate ? 'disable' : ''}`} onClick={() => setOpenDate(true)}>
                  <TextInput disabled={openDate} placeholder={t('_select_date')} defaultValue={valueDate ? valueDate : ''} />
                </div>
                {openDate && (
                  <div className="orders-date-picker" ref={dateRef}>
                    <DatePicker
                      {...register('date')}
                      dateFormat="YYYY-MM-DD"
                      selected={startDate}
                      onChange={onDateChange}
                      startDate={startDate}
                      endDate={endDate}
                      monthsShown={isMobile ? 1 : 2}
                      selectsRange
                      inline
                      locale={i18n.language.split('-')[0]}
                    />
                  </div>
                )}
              </div>
              <div className="orders-filter-input-item">
                <TextInput {...register('id')} placeholder={t('_by_id')} />
              </div>
              <div className="orders-filter-input-item">
                <Controller
                  control={control}
                  name="customer_id"
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Select
                        value={value}
                        className="select-dropdown"
                        onChange={(value) => onChange(value)}
                        options={clients}
                        placeholder={t('_client')}
                        styles={{
                          menu: provided => ({ ...provided, zIndex: 50 })
                        }}
                        isSearchable={!isMobile}
                      />
                    );
                  }}
                />
              </div>

              <DefaultButton text={t('_filter')} type="submit" />
              <DefaultButton red text={t('_reset')} onClick={handleResetFilter} />
            </div>
          </form>
        </div>
        <div className="orders-table">

          <OrderTable data={data} getClientOrderList={getClientOrderList} handlePageClick={handlePageClick} setOpenDetails={setOpenDetails} currentPage={currentPage} totalPage={totalPage} loading={loading} hiddenColumns={hiddenColumns} orderAction={orderAction} setOrderAction={setOrderAction} />
        </div>

      </div>
      : <ClientOrderDetails setOpenDetails={setOpenDetails} getClientOrderList={getClientOrderList} orderAction={orderAction} setOrderAction={setOrderAction} />
  );
};

export default ClientOrderList;
